
import {
    bindKey,
    unbindKey
} from '../../js/window'


/**
 * Overlay with z-index above rest of page, positioned relative to anchor.
 */
export default {
    props: {
        anchor: {
            Object,
            Element
        },
        offsetx: {
            type: Number,
            default: 0
        },
        offsety: {
            type: Number,
            default: 0
        },
        width: String,
        halign: String,
        valign: String,
        persistent: Boolean
    },

    data() {
        return {
            overlay: true,
            visible: false,
            wasVisible: false,
            scrollParents: [],
            iosX: null,
            iosY: null
        };
    },

    watch: {
        anchor(anchor) {
            this.anchored(anchor);
        }
    },

    mounted() {
        this.$el.classList.add('hidden');
        this.anchored(this.anchor);
        this.refresh();
    },

    methods: {
        /** Called on any change in open/close state, anchor, or scroll position */

        refresh() {
            const el = this.$el;
            let right = this.halign == 'right' ? true : this.halign == 'left' ? false : null;
            let below = this.valign == 'bottom' ? true : this.valign == 'top' ? false : null;

            if (this.wasVisible) {
                const html = document.documentElement;
                const height = this.$el.offsetHeight;
                const anchor = this.anchor;
                const rect = anchor
                    ? (anchor.$el || anchor).getBoundingClientRect()
                    : {     // no anchor = relative to window frame
                        bottom: 0,
                        right: html.clientWidth,
                        top: html.clientHeight,
                        left: 0
                    };
                if (below == null) {
                    const dy = html.clientHeight - (rect.bottom + height);
                    below = dy >= 0 || dy >= rect.top - height;
                }

                // Hide overlay if anchor is out of view
                let i = false;
                if (anchor) {
                    el.style.pointerEvents = 'none';
                    if (!$util.isChildOrSelf(document.elementFromPoint(
                            rect.left + rect.right >> 1,
                            below ? rect.bottom - 1 : rect.top),
                            anchor.$el || anchor)) {
                        el.style.visibility = 'hidden';
                        i = true;
                    }
                    el.style.pointerEvents = '';
                }

                // Show overlay; set position and dimensions
                if (!i) {
                    if (right) {
                        el.style.left = 'auto';
                        el.style.right = html.clientWidth - rect.right - this.offsetx + 'px';
                        el.style.maxWidth = rect.right + this.offsetx + 'px';
                    } else {
                        el.style.left = rect.left + this.offsetx + 'px';
                        el.style.right = 'auto';
                        el.style.maxWidth = html.clientWidth - rect.left - this.offsetx + 'px';
                    }
                    if (below) {
                        el.style.top = rect.bottom + this.offsety + 'px';
                        el.style.bottom = 'auto';
                        el.style.maxHeight = html.clientHeight - rect.bottom - this.offsety + 'px';
                        el.classList.remove('_ab');
                    } else {
                        el.style.top = 'auto';
                        el.style.bottom = html.clientHeight - rect.top - this.offsety + 'px';
                        el.style.maxHeight = rect.top + this.offsety + 'px';
                        el.classList.add('_ab');
                    }
                    if (this.width) {
                        el.style.width = this.width == 'inherit' ? rect.right - rect.left + 'px' : this.width;
                    }
                    el.style.visibility = '';
                    return;
                }
            }

            if (right) {
                el.style.left = 'auto';
                el.style.right = '0px';
            } else {
                el.style.left = '0px';
                el.style.right = 'auto';
            }
            if (below) {
                el.style.top = '0px';
                el.style.bottom = 'auto';
            } else {
                el.style.top = 'auto';
                el.style.bottom = '0px';
            }
            el.style.maxWidth = '';
            el.style.maxHeight = '';
            if (this.width) {
                el.style.width = '';
            }
        },

        /** Make overlay visible */

        open() {
            if (!this.visible) {
                this.visible = true;
                this.wasVisible = true;
                this.refresh();
                if (this.onShow) {
                    this.onShow();
                }
                if (!this.persistent) {
                    document.addEventListener('mousedown', this.dismiss, true);
                    document.addEventListener('touchstart', this.dismiss, true);
                }
                addEventListener('resize', this.refresh);
                bindKey('Escape', this.close);
                this.$el.classList.remove('hidden');

                // Workaround: iOS 11 incorrect caret position [WebKit bug 176896]
                if (/iP(hone|ad|od).*OS 11_[012]/.test(navigator.userAgent)) {
                    this.$nextTick(() => {
                        if (this.$el.querySelector('input')) {
                            this.iosX = window.pageXOffset;
                            this.iosY = window.pageYOffset;
                            document.body.style.position = 'fixed';
                            document.body.style.left = -this.iosX + 'px';
                            document.body.style.top = -this.iosY + 'px';
                        }
                    });
                }
            }
        },

        /** Make overlay invisible */

        close() {
            if (this.visible) {
                this.visible = false;
                if (this.onHide) {
                    this.onHide();
                }
                if (!this.persistent) {
                    document.removeEventListener('mousedown', this.dismiss, true);
                    document.removeEventListener('touchstart', this.dismiss, true);
                }
                removeEventListener('resize', this.refresh);
                unbindKey('Escape', this.close);
                this.$el.classList.add('hidden');

                // Workaround: iOS 11 incorrect caret position
                if (this.iosX != null) {
                    window.scrollTo(this.iosX, this.iosY);
                    document.body.style.position = '';
                    document.body.style.top = '';
                    document.body.style.left = '';
                    this.iosX = null;
                    this.iosY = null;
                }
            }
        },

        /** Toggle visibility state */

        toggle() {
            if (this.visible) {
                this.close();
            } else {
                this.open();
            }
        },

        /** Make overlay invisible due to touch or click outside overlay and anchor */

        dismiss(e) {
            if (!this.$util.isInside(this.$el, e.clientX, e.clientY) && e.target !== this.$el && !this.$util.isChild(e.target, this.$el)) {
                const anchor = this.anchor ? (this.anchor.$el || this.anchor) : null;
                if (!anchor || (e.target !== anchor && !this.$util.isChild(e.target, anchor))) {
                    this.close();
                }
            }
        },

        /** Notify change in anchor */

        anchored(anchor) {
            let sp = this.scrollParents;
            for (let i = 0; i < sp.length; i++) {
                sp[i].removeEventListener('scroll', this.refresh);
            }
            if (anchor) {
                sp = this.$util.scrollParents(anchor.$el || anchor);
                for (let i = 0; i < sp.length; i++) {
                    sp[i].addEventListener('scroll', this.refresh);
                }
            } else {
                sp = [];
            }
            this.scrollParents = sp;
            this.$nextTick(this.refresh);
        }
    }
}
